import React from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { api } from "@/lib/api";

export default function PortalPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [user, setUser] = React.useState(location.state?.user || null);
  const [loading, setLoading] = React.useState(!location.state?.user);
  const [dash, setDash] = React.useState(null);

  const loadDashboard = React.useCallback(async () => {
    if (!user) {
      try {
        const { data } = await api.get("/auth/me");
        setUser(data);
      } catch (err) {
        // Unauthenticated — bounce to login.
        void err;
        navigate("/login");
        return;
      } finally {
        setLoading(false);
      }
    }
    try {
      const { data } = await api.get("/portal/dashboard");
      setDash(data);
    } catch (err) {
      // Dashboard fetch is best-effort; leave panels empty.
      void err;
    }
  }, [navigate, user]);

  React.useEffect(() => {
    loadDashboard();
    // Run once on mount — re-fetch on subsequent renders is undesired.
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch (err) {
      // Logout API failure is non-blocking — proceed home regardless.
      void err;
    }
    navigate("/");
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center text-secondary text-xs uppercase tracking-widest">Loading…</div>;
  if (!user) return null;

  return (
    <main className="pt-32 pb-24 min-h-screen">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex justify-between items-end flex-wrap gap-6">
          <div>
            <div className="overline">Client Portal</div>
            <h1 className="font-display font-light text-4xl md:text-6xl mt-3" data-testid="portal-welcome">Welcome, <em className="text-brand">{user.name?.split(' ')[0] || "Friend"}</em>.</h1>
            <p className="text-secondary mt-2">{user.email}</p>
          </div>
          <button onClick={logout} className="btn-ghost" data-testid="portal-logout">Log Out →</button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-16">
          <div className="editorial-card p-8 tracing-beam"><div className="overline">Next Meeting</div><div className="font-display text-2xl mt-3">Book a session</div><a href="/#contact" className="btn-ghost mt-4 inline-flex">Schedule →</a></div>
          <div className="editorial-card p-8"><div className="overline">Open Support</div><div className="font-display text-2xl mt-3">{dash?.support_open ?? 0} tickets</div></div>
          <div className="editorial-card p-8"><div className="overline">Trainings Completed</div><div className="font-display text-2xl mt-3">{dash?.trainings_completed ?? 0}</div></div>
        </div>

        <div className="mt-16">
          <div className="overline">Quick Actions</div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mt-6">
            {[
              {label: "Request Support", href: "/#contact"},
              {label: "View Invoices", href: "#"},
              {label: "Book a Meeting", href: "/#contact"},
              {label: "AI Academy", href: "#"},
            ].map((a)=>(
              <a key={a.label} href={a.href} className="editorial-card p-6 flex justify-between items-center" data-testid={`portal-action-${a.label.toLowerCase().replace(/\s+/g,'-')}`}>
                <span className="font-display text-lg">{a.label}</span>
                <span className="text-brand">→</span>
              </a>
            ))}
          </div>
        </div>

        <div className="mt-16">
          <div className="overline">Invoices</div>
          {dash?.invoices?.length ? (
            <div className="mt-6 border-t border-white/5">
              {dash.invoices.map((iv,i)=>(
                <div key={iv.id || iv.session_id || i} className="py-5 border-b border-white/5 flex justify-between">
                  <div><div className="font-display text-lg">{iv.metadata?.package_name || iv.package_id}</div><div className="text-muted text-xs uppercase tracking-widest mt-1">{iv.status}</div></div>
                  <div className="font-display text-xl text-brand">${iv.amount}</div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-secondary mt-4">No invoices yet. Once we kick off your first project, you'll see them here.</p>
          )}
        </div>
      </div>
    </main>
  );
}
